import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enGB } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./calendar-theme.css";

import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import MapView from "@/components/schedule/MapView";
import { Button } from "@/components/ui/button";
import { CalendarZoom, loadZoom, useCalendarRow } from "./CalendarZoom";
import { Loader2, Plus, ChevronLeft, ChevronRight, Users, PanelRightClose, PanelRightOpen, ChevronDown, AlertTriangle, Inbox, CalendarDays, Megaphone } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DatePicker } from "@/components/ui/calendar";
import { toast } from "sonner";
import { toDate, fmtDate, fmtTime, eventColors } from "@/lib/calendarUtils";
import EventCreateDialog from "./EventCreateDialog";
import EventDetailDialog from "./EventDetailDialog";
import WarningsPanel from "./WarningsPanel";
import UnassignedTray from "./UnassignedTray";
import QueuePanel from "./QueuePanel";
import CommunalPanel from "./CommunalPanel";

const localizer = dateFnsLocalizer({ format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 0 }), getDay, locales: { "en-GB": enGB } });
const DnDCalendar = withDragAndDrop(Calendar);
const VIEW_LIST = [Views.DAY, Views.WEEK, Views.MONTH, Views.AGENDA];

export default function CalendarBoard() {
  const { can, user } = useAuth();
  const { t } = useI18n();
  const [raw, setRaw] = useState([]);
  const [notices, setNotices] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [groupByWorker, setGroupByWorker] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [slotDefault, setSlotDefault] = useState(null);
  const forceDesktop = typeof document !== "undefined" && document.documentElement.classList.contains("force-desktop");
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!detail) { client.get("/day-notices").then((r) => setNotices(r.data?.notices || [])).catch(() => {}); }
  }, [detail]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [warnOpen, setWarnOpen] = useState(false);
  const [unassignedOpen, setUnassignedOpen] = useState(true);
  const [mapView, setMapView] = useState(false);
  const [kindFilter, setKindFilter] = useState("all"); // all | field | connect
  const [zoom, setZoom] = useState(loadZoom);
  const [queue, setQueue] = useState([]);
  const [queueOnMap, setQueueOnMap] = useState(true);
  const [dragEvent, setDragEvent] = useState(null);
  const [calKey, setCalKey] = useState(0);
  const [dropMode, setDropMode] = useState("move");
  const [communalMode, setCommunalMode] = useState("copy");
  const [communal, setCommunal] = useState([]);
  const loadCommunal = useCallback(() => { client.get("/communal-list").then((r) => setCommunal(r.data || [])).catch(() => setCommunal([])); }, []);
  useEffect(() => { if (can("property.view")) loadCommunal(); }, [loadCommunal, can]);
  const boardRef = useRef(null);
  const loadQueue = useCallback(() => { client.get("/job-queue").then((r) => setQueue(r.data || [])).catch(() => setQueue([])); }, []);
  useEffect(loadQueue, [loadQueue]);
  useEffect(() => {
    if (!dragEvent) { return undefined; }
    const clear = () => setTimeout(() => setDragEvent(null), 0);
    document.addEventListener("mouseup", clear);
    return () => document.removeEventListener("mouseup", clear);
  }, [dragEvent]);
  const queueEvent = async (ev) => {
    setDragEvent(null);
    if (ev.kind !== "job") { toast.error("Only field jobs can be queued"); return; }
    try { await client.post(`/jobs/${ev.id}/queue`); toast.success("Moved to the queue"); load(); loadQueue(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const canEditAny = can("schedule.edit") || can("job.manage");

  const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
  const reqId = useRef(0);
  useCalendarRow(boardRef, zoom, [view, mapView, loading, panelOpen, groupByWorker]);

  const load = useCallback(() => {
    setLoading(true);
    const my = ++reqId.current;
    const [y, m] = monthKey.split("-").map(Number);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const from = iso(new Date(y, m, -13)), to = iso(new Date(y, m + 1, 31));
    Promise.all([
      client.get(`/calendar?start=${from}&end=${to}`),
      client.get("/calendar/warnings"),
      can("user.view") ? client.get("/users") : Promise.resolve({ data: [] }),
      client.get("/sites"),
      can("property.view") ? client.get("/properties") : Promise.resolve({ data: [] }),
      can("client.view") ? client.get("/clients") : Promise.resolve({ data: [] }),
      client.get("/day-notices").catch(() => ({ data: { notices: [] } })),
    ]).then(([ev, wn, us, si, pr, cl, dn]) => {
      if (my !== reqId.current) { return; }
      setNotices(dn.data?.notices || []);
      setRaw(ev.data);
      setWarnings(wn.data);
      const allUsers = us.data || [];
      setWorkers(allUsers.filter((u) => (u.role === "cleaner" || u.role === "supervisor") && u.status !== "deactivated" && !/^test[\s_]/i.test(u.displayName || "")));
      setAdmins(allUsers.filter((u) => ["owner", "admin", "office"].includes(u.role) && u.status !== "deactivated"));
      setSites(si.data || []);
      setProperties(pr.data || []);
      setClients(cl.data || []);
    }).catch(() => {}).finally(() => { if (my === reqId.current) { setLoading(false); } });
    client.get("/job-types").then((r) => setJobTypes(r.data || [])).catch(() => {});
  }, [can, monthKey]);
  useEffect(load, [load]);

  const createSite = async (name) => {
    const { data } = await client.post("/sites", { name, address: "", geo: { lat: null, lng: null } });
    const list = (await client.get("/sites")).data; setSites(list);
    return data?.id || (list.find((s) => s.name === name) || {}).id;
  };
  const createProperty = async (address, clientId = "") => {
    const { data } = await client.post("/properties", { clientId: clientId || "", address, geofenceRadius: 25, geo: { lat: null, lng: null } });
    const list = (await client.get("/properties")).data; setProperties(list);
    return data?.id || (list.find((p) => p.address === address) || {}).id;
  };
  const createPropertyFromAddress = async (r, clientId = "") => {
    const { data } = await client.post("/properties", { clientId: clientId || "", address: r.address, geofenceRadius: 25, geo: { lat: r.lat ?? null, lng: r.lng ?? null } });
    const list = (await client.get("/properties")).data; setProperties(list);
    return data?.id || (list.find((p) => p.address === r.address) || {}).id;
  };
  const createClient = async (name) => {
    const { data } = await client.post("/clients", { name, contact: "" });
    const list = (await client.get("/clients")).data; setClients(list);
    return data?.id || (list.find((c) => c.name === name) || {}).id;
  };
  const openDuplicate = (event) => {
    setPrefill({
      clientId: event.clientId || "", propertyId: event.propertyId || "",
      workerIds: event.workerIds || (event.workerId ? [event.workerId] : []),
      salesmanId: event.salesmanId || "", jobType: event.jobType || "one_off", jobTypeOther: event.jobTypeOther || "",
      date: event.date, startTime: event.startTime, endTime: event.endTime,
      instructions: event.instructions || "", notes: event.notes || "",
    });
    setDetail(null);
    setCreateOpen(true);
  };

  const warnedIds = useMemo(() => {
    const s = new Set();
    warnings.forEach((w) => (w.eventIds || []).forEach((id) => s.add(id)));
    return s;
  }, [warnings]);

  const warnTypes = useMemo(() => {
    const m = {};
    warnings.forEach((w) => (w.eventIds || []).forEach((id) => { (m[id] = m[id] || new Set()).add(w.type); }));
    const out = {};
    Object.keys(m).forEach((k) => { out[k] = Array.from(m[k]); });
    return out;
  }, [warnings]);

  const visibleRaw = useMemo(() => raw.filter((e) =>
    kindFilter === "all" || (kindFilter === "field" ? e.kind === "job" : e.kind === "shift")
  ), [raw, kindFilter]);

  const events = useMemo(() => {
    const base = (e) => ({ ...e, start: toDate(e.date, e.startTime), end: toDate(e.date, e.endTime), resourceId: e.workerId || "__none__" });
    if (groupByWorker) return visibleRaw.map(base);
    const out = [], groups = {};
    visibleRaw.forEach((e) => {
      if (e.kind !== "shift") { out.push(base(e)); return; }
      const k = `${e.siteId}|${e.date}|${e.startTime}|${e.endTime}`;
      if (!groups[k]) { groups[k] = { ...base(e), shiftIds: [], workers: [], workerIds: [], workerColour: null }; out.push(groups[k]); }
      const g = groups[k];
      g.shiftIds.push(e.id);
      if (!g.seriesId && e.seriesId) { g.seriesId = e.seriesId; g.recurrence = g.recurrence || e.recurrence; }
      if (e.workerId && !g.workerIds.includes(e.workerId)) { g.workerIds.push(e.workerId); g.workers.push({ id: e.workerId, name: e.workerName, status: e.status }); }
      if (e.status === "active") g.status = "active";
    });
    out.forEach((g) => { if (g.shiftIds) g.title = `${g.siteNumber ? `#S${g.siteNumber} · ` : ""}${g.siteName || g.title}${g.workers.length ? ` · ${g.workers.length} cleaner${g.workers.length === 1 ? "" : "s"}` : ""}`; });
    return out;
  }, [visibleRaw, groupByWorker]);

  const unassigned = useMemo(() => visibleRaw.filter((e) => {
    if (e.kind === "shift") return !e.workerId && e.status === "scheduled";
    if (["cancelled", "completed"].includes(e.status)) return false;
    return (e.workers?.length || 0) === 0;
  }), [visibleRaw]);

  const noticeMap = useMemo(() => {
    const bySite = {}, byDay = {};
    notices.forEach((n) => {
      (bySite[`${n.siteId}|${n.date}`] = bySite[`${n.siteId}|${n.date}`] || []).push(n);
      (byDay[n.date] = byDay[n.date] || []).push(n);
    });
    return { bySite, byDay };
  }, [notices]);

  const calComponents = useMemo(() => {
    const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const DayBadge = ({ date }) => {
      const list = noticeMap.byDay[dayKey(date)] || [];
      if (!list.length) return null;
      return (
        <span className="inline-flex items-center gap-0.5 ml-1 rounded-full bg-sky-100 text-sky-700 px-1.5 text-[10px] font-semibold align-middle"
          title={list.map((n) => `${n.siteName}: ${n.title}`).join("\n")} data-testid={`cal-day-notice-${dayKey(date)}`}>
          <Megaphone className="h-3 w-3" />{list.length}
        </span>
      );
    };
    return {
      event: ({ event }) => {
        const has = event.kind === "shift" && noticeMap.bySite[`${event.siteId}|${event.date}`]?.length;
        const isJob = event.kind === "job";
        const num = isJob ? (event.jobNumber ? `#${event.jobNumber} · ` : "") : (event.siteNumber ? `#S${event.siteNumber} · ` : "");
        const place = isJob ? (event.propertyAddress || event.address) : event.siteName;
        const second = isJob ? event.clientName : null;
        const names = (event.workers?.length ? event.workers.map((w) => w.name) : [event.workerName]).filter(Boolean)
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).join(", ");
        return (
          <div className="cal-card" data-testid={`cal-event-${event.id}`}>
            <div className="cal-card-time">{event.startTime} - {event.endTime}</div>
            {isJob && !(event.workerIds?.length) && !["completed", "cancelled"].includes(event.status) && (
              <div className="font-bold text-red-600" data-testid={`cal-event-unassigned-${event.id}`}>Unassigned</div>
            )}
            <div className="cal-card-title">
              {has ? <Megaphone className="inline h-3 w-3 mr-0.5 -mt-0.5" data-testid={`cal-event-notice-${event.id}`} /> : null}
              <span className={event.status === "completed" ? "line-through decoration-2" : ""}>{num}{place || event.title}</span>
            </div>
            {second && <div className="cal-card-line" data-testid={`cal-event-client-${event.id}`}>{second}</div>}
            {names && <div className="cal-card-line cal-card-names" data-testid={`cal-event-workers-${event.id}`}>{names}</div>}
          </div>
        );
      },
      header: ({ date, label }) => <span>{label}<DayBadge date={date} /></span>,
      month: {
        dateHeader: ({ date, label }) => <span>{label}<DayBadge date={date} /></span>,
        event: ({ event }) => (
          <div className="truncate text-[11px] leading-tight" data-testid={`cal-event-${event.id}`}>
            <span className="font-semibold">{event.startTime}</span>{" "}
            {event.kind === "job" && !(event.workerIds?.length) && <span className="mr-1 font-bold text-red-600" data-testid={`cal-event-unassigned-${event.id}`}>Unassigned</span>}
            <span className={event.status === "completed" ? "line-through" : ""}>{event.title}</span>
          </div>
        ),
      },
    };
  }, [noticeMap]);

  const resources = useMemo(() => {
    if (!groupByWorker) return undefined;
    const list = workers.map((w) => ({ id: w.id, title: w.displayName }));
    list.push({ id: "__none__", title: t("job.unassigned", "Unassigned") });
    return list;
  }, [groupByWorker, workers, t]);

  const eventStyleGetter = (event) => {
    const c = eventColors(event.kind, event.status === "completed" ? "scheduled" : event.status);
    const done = ["completed", "cancelled"].includes(event.status);
    const unassigned = event.kind === "job" && !(event.workerIds?.length) && !done;
    const custom = event.status === "cancelled" ? null : event.kind === "job" ? event.workerColour : event.siteColour;
    const useWorker = !!custom;
    const bg = unassigned ? "#e5e7eb" : useWorker ? custom : c.bg;
    const border = unassigned ? "#9ca3af" : useWorker ? custom : c.border;
    const light = useWorker && (() => {
      const h = custom.replace("#", "");
      const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
      return 0.299 * r + 0.587 * g + 0.114 * b > 170;
    })();
    return {
      style: { backgroundColor: bg, borderLeft: `5px solid color-mix(in srgb, ${useWorker ? bg : border} 72%, #000)`, color: unassigned ? "#334155" : useWorker ? (light ? "#0f172a" : "#fff") : c.text,
        opacity: event.status === "cancelled" ? 0.65 : 1,
        textDecoration: event.status === "cancelled" ? "line-through" : "none" },
    };
  };

  const canDrag = (event) =>
    canEditAny && ["scheduled", "active"].includes(event.status) &&
    (event.kind === "job" ? can("job.manage") : can("schedule.edit"));

  const runCheck = async (ev, workerId, date, startTime, endTime) => {
    if (!workerId) return false;
    try {
      const { data } = await client.post("/calendar/check", {
        kind: ev.kind, workerId, siteId: ev.siteId || null, propertyId: ev.propertyId || null,
        date, startTime, endTime, excludeId: ev.id,
      });
      const parts = [];
      if (data.conflicts?.length) parts.push(`${data.conflicts.length}× ${t("cal.warn.double_book", "Double booking")}`);
      if (data.leave?.length) parts.push(t("cal.warn.leave", "Leave conflict"));
      if (data.availability?.length) parts.push(t("cal.warn.availability", "Availability"));
      if (data.travel?.length) parts.push(`${t("cal.warn.travel", "Travel time")} (~${data.travel[0].km}km/${data.travel[0].needMin}min)`);
      if (parts.length) toast.warning(`${t("warn.assignTitle", "Scheduling conflict")}: ${parts.join(" · ")}`);
      return parts.length > 0;
    } catch { return false; }
  };

  const applyMove = async (event, start, end, resourceId) => {
    const date = fmtDate(start), startTime = fmtTime(start), endTime = fmtTime(end);
    const workerId = resourceId !== undefined ? (resourceId === "__none__" ? null : resourceId) : event.workerId;
    const body = { date, startTime, endTime, applyToSeries: false };
    if (event.shiftIds) {
      try {
        await client.patch("/calendar/shift-group", { ...body, shiftIds: event.shiftIds });
        toast.success(t("toast.eventUpdated", "Updated")); load();
      } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
      return;
    }
    await runCheck(event, workerId, date, startTime, endTime);
    if (resourceId !== undefined) body.workerId = workerId;
    try {
      await client.patch(`/calendar/event/${event.kind}/${event.id}`, body);
      toast.success(t("toast.eventUpdated", "Updated"));
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const onEventDrop = ({ event, start, end, resourceId }) => applyMove(event, start, end, resourceId);
  const onEventResize = ({ event, start, end }) => applyMove(event, start, end);

  const handleSelectSlot = ({ start, end, resourceId }) => {
    if (!canEditAny) return;
    const startTime = fmtTime(start), endTime = fmtTime(end);
    const allDay = startTime === endTime;
    setSlotDefault({
      date: fmtDate(start),
      startTime: allDay ? "09:00" : startTime,
      endTime: allDay ? "12:00" : endTime,
      workerId: resourceId && resourceId !== "__none__" ? resourceId : undefined,
    });
    setCreateOpen(true);
  };

  const onDropFromOutside = async ({ start, resource }) => {
    const it = draggedItem;
    setDraggedItem(null);
    if (!it) return;
    if (it.kind === "queue" || it.kind === "communal") {
      setCalKey((k) => k + 1);
      const st = fmtTime(start);
      const body = { date: fmtDate(start), startTime: st === "00:00" ? "09:00" : st, workerId: resource && resource !== "__none__" ? resource : null, keep: (it.kind === "queue" ? dropMode : communalMode) === "copy" };
      const url = it.kind === "queue" ? `/job-queue/${it.id}/schedule` : `/communal-list/${it.id}/schedule`;
      try {
        const { data } = await client.post(url, body);
        toast.success(`Booked ${data.date} ${data.startTime}–${data.endTime}${data.kept ? " · kept on the list" : ""}`);
        load(); loadQueue(); loadCommunal();
      } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
      return;
    }
    if (!resource || resource === "__none__") { toast.error(t("tray.dropHint", "Switch to By-worker Day or Week view to assign by dragging.")); return; }
    const durMs = toDate(it.date, it.endTime).getTime() - toDate(it.date, it.startTime).getTime();
    const date = fmtDate(start), startTime = fmtTime(start);
    const endTime = fmtTime(new Date(start.getTime() + Math.max(durMs, 30 * 60000)));
    const isPartialJob = it.kind === "job" && (it.workerIds?.length || 0) > 0;
    const body = isPartialJob
      ? { workerIds: Array.from(new Set([...(it.workerIds || []), resource])), date: it.date, startTime: it.startTime, endTime: it.endTime, applyToSeries: false }
      : { workerId: resource, date, startTime, endTime, applyToSeries: false };
    await runCheck(it, resource, body.date, body.startTime, body.endTime);
    try {
      await client.patch(`/calendar/event/${it.kind}/${it.id}`, body);
      toast.success(t("toast.eventUpdated", "Updated"));
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const messages = {
    today: t("cal.today", "Today"), previous: t("cal.back", "Back"), next: t("cal.next", "Next"),
    day: t("view.day", "Day"), week: t("view.week", "Week"), month: t("view.month", "Month"),
    agenda: t("view.agenda", "List"), work_week: t("view.work_week", "Work week"),
    showMore: (n) => `+${n}`,
  };

  const label = format(date, view === Views.DAY ? "EEEE d MMM yyyy" : "MMMM yyyy");
  const supportsResources = groupByWorker && (view === Views.DAY || view === Views.WEEK);

  return (
    <div className={`grid grid-cols-1 gap-4 ${panelOpen ? "lg:grid-cols-[1fr_320px]" : "lg:grid-cols-1"}`}>
      <div className="space-y-2">
        {/* toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" data-testid="cal-prev" onClick={() => setDate((d) => { const n = new Date(d); n.setDate(n.getDate() - (view === Views.MONTH ? 30 : view === Views.WEEK ? 7 : 1)); return n; })}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" data-testid="cal-today" onClick={() => setDate(new Date())}>{t("cal.today", "Today")}</Button>
            <Button size="icon" variant="outline" data-testid="cal-next" onClick={() => setDate((d) => { const n = new Date(d); n.setDate(n.getDate() + (view === Views.MONTH ? 30 : view === Views.WEEK ? 7 : 1)); return n; })}><ChevronRight className="h-4 w-4" /></Button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="cal-date-picker-trigger"
                  className="ml-2 inline-flex items-center gap-1.5 font-display font-semibold text-slate-900 hover:text-brand transition rounded-md px-1.5 py-1 hover:bg-slate-50"
                  title={t("cal.jumpToDate", "Jump to date")}
                >
                  {label}
                  <CalendarDays className="h-4 w-4 text-slate-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0" data-testid="cal-date-picker-popover">
                <DatePicker
                  mode="single"
                  selected={date}
                  defaultMonth={date}
                  onSelect={(d) => { if (d) setDate(d); }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="cal-kind-filter">
              {["all", "field", "connect"].map((k) => (
                <button key={k} data-testid={`cal-kind-${k}`} onClick={() => setKindFilter(k)}
                  className={`px-3 py-1.5 text-xs font-semibold capitalize ${kindFilter === k ? (k === "field" ? "bg-brand text-white" : k === "connect" ? "bg-teal-600 text-white" : "bg-slate-900 text-white") : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {t(`cal.kind.${k}`, { all: "All", field: "Field", connect: "Connect" }[k])}
                </button>
              ))}
            </div>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="cal-view-switcher">
              {VIEW_LIST.map((v) => (
                <button key={v} data-testid={`cal-view-${v}`} onClick={() => { setView(v); setMapView(false); }}
                  className={`px-3 py-1.5 text-xs font-semibold ${view === v && !mapView ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {messages[v]}
                </button>
              ))}
              <button data-testid="cal-view-map" onClick={() => setMapView(true)}
                className={`px-3 py-1.5 text-xs font-semibold ${mapView ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {t("cal.view.map", "Map")}
              </button>
            </div>
            <Button size="sm" variant={groupByWorker ? "default" : "outline"} data-testid="cal-groupby-toggle"
              className={groupByWorker ? "bg-slate-900" : ""} onClick={() => setGroupByWorker((g) => !g)}>
              <Users className="h-4 w-4 mr-1.5" />{t("cal.groupByWorker", "By worker")}
            </Button>
            {!mapView && (view === Views.WEEK || view === Views.DAY) && <CalendarZoom zoom={zoom} setZoom={setZoom} />}
            <Button size="icon" variant="outline" data-testid="cal-panel-toggle" onClick={() => setPanelOpen((p) => !p)}
              title={panelOpen ? t("cal.panelHide", "Hide panel") : t("cal.panelShow", "Show panel")}>
              {panelOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
            </Button>
            {canEditAny && (
              <Button size="sm" className="bg-brand hover:bg-brand-dark" data-testid="cal-new-btn" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" />{t("cal.new", "New")}
              </Button>
            )}
          </div>
        </div>

        {mapView ? <MapView events={visibleRaw} onOpenDetails={setDetail} queued={queueOnMap ? queue : []} /> : (
        <Card ref={boardRef} className="border-slate-200 p-2 cal-zoom" data-testid="calendar-board">
          {loading && !raw.length ? (
            <div className="h-[70vh] flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <DnDCalendar
              key={calKey}
              localizer={localizer}
              culture="en-GB"
              events={events}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              views={VIEW_LIST}
              messages={messages}
              toolbar={false}
              style={{ height: forceDesktop ? "calc(100dvh - 275px)" : "calc(100dvh - 200px)", minHeight: forceDesktop ? 300 : 420 }}
              popup
              step={60}
              timeslots={1}
              min={new Date(1970, 0, 1, 0, 0)}
              max={new Date(1970, 0, 1, 23, 59, 59)}
              scrollToTime={new Date(1970, 0, 1, 8, 0)}
              resources={supportsResources ? resources : undefined}
              resourceIdAccessor="id"
              resourceTitleAccessor="title"
              draggableAccessor={canDrag}
              resizableAccessor={canDrag}
              onEventDrop={onEventDrop}
              onEventResize={onEventResize}
              onDropFromOutside={onDropFromOutside}
              onDragStart={({ event }) => setDragEvent(event)}
              dragFromOutsideItem={draggedItem ? () => (draggedItem.kind === "queue" || draggedItem.kind === "communal" ? { title: draggedItem.address } : { title: draggedItem.title }) : undefined}
              onSelectEvent={(e) => setDetail(e)}
              selectable={canEditAny}
              onSelectSlot={handleSelectSlot}
              eventPropGetter={eventStyleGetter}
              components={calComponents}
              tooltipAccessor={(e) => `${e.title} · ${e.startTime}–${e.endTime} · ${t(`st.${e.status}`, e.status)}`}
            />
          )}
        </Card>
        )}
      </div>

      {panelOpen && (
        <div className="space-y-3 lg:sticky lg:top-[68px] lg:max-h-[calc(100vh-96px)] overflow-y-auto pr-1" data-testid="calendar-side-panel">
          {can("job.view") && (
            <QueuePanel items={queue} canManage={can("job.manage")} clients={clients} properties={properties} onChanged={loadQueue}
              onDragStart={(it) => setDraggedItem({ ...it, kind: "queue" })} onDragEnd={() => setDraggedItem(null)}
              draggingEvent={dragEvent} onDropEvent={queueEvent} showOnMap={queueOnMap} setShowOnMap={setQueueOnMap} dropMode={dropMode} setDropMode={setDropMode} />
          )}
          {can("property.view") && (
            <CommunalPanel items={communal} canManage={can("job.manage")} dropMode={communalMode} setDropMode={setCommunalMode}
              onDragStart={(p) => setDraggedItem({ ...p, kind: "communal" })} onDragEnd={() => setDraggedItem(null)} />
          )}
          <button data-testid="warnings-fold-toggle" onClick={() => setWarnOpen((w) => !w)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />{t("cal.warnings", "Warnings")} ({warnings.length})</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${warnOpen ? "rotate-180" : ""}`} />
          </button>
          {warnOpen && <WarningsPanel warnings={warnings} onFocus={(w) => { if (w.date) setDate(new Date(`${w.date}T00:00:00`)); }} />}
          <button data-testid="unassigned-fold-toggle" onClick={() => setUnassignedOpen((u) => !u)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <span className="flex items-center gap-2"><Inbox className="h-4 w-4 text-brand" />{t("tray.title", "Unassigned")} ({unassigned.length})</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${unassignedOpen ? "rotate-180" : ""}`} />
          </button>
          {unassignedOpen && (
            <UnassignedTray items={unassigned} warnedIds={warnedIds} warnTypes={warnTypes} groupByWorker={groupByWorker} view={view}
              onDragStart={setDraggedItem} onDragEnd={() => setDraggedItem(null)} />
          )}
        </div>
      )}

      <EventCreateDialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setSlotDefault(null); setPrefill(null); } }} workers={workers} sites={sites}
        properties={properties} clients={clients} admins={admins} jobTypes={jobTypes} currentUserId={user?.id} prefill={prefill}
        defaultDate={fmtDate(date)} defaultSlot={slotDefault} can={can} onCreated={load}
        onCreateSite={can("site.manage") ? createSite : undefined} onCreateProperty={can("property.manage") ? createProperty : undefined}
        onCreatePropertyFromAddress={can("property.manage") ? createPropertyFromAddress : undefined}
        onCreateClient={can("client.manage") ? createClient : undefined} />
      <EventDetailDialog event={detail} onOpenChange={(o) => !o && setDetail(null)} workers={workers}
        can={can} onUpdated={(keepOpen, changes) => { if (keepOpen === true) setDetail((d) => (d ? { ...d, ...changes } : d)); else setDetail(null); load(); }} onDuplicate={openDuplicate}
        onQueue={can("job.manage") ? (ev) => { setDetail(null); queueEvent(ev); } : undefined} />
    </div>
  );
}
